import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Mail, ArrowLeft, HelpCircle } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import SEO from '../components/SEO';

const FAQ = () => {
  const faqs = [
    {
      question: "What is Instant-Email?",
      answer: "Instant-Email is a free service that provides temporary, disposable email addresses. You can use these addresses to receive emails without revealing your real email address, protecting your privacy and inbox from spam."
    },
    {
      question: "How do I get a temporary email address?",
      answer: "Simply visit our homepage at instant-email.org and you'll instantly receive a temporary email address. No registration, no signup, and no personal information required. Just copy the address and start using it right away."
    },
    {
      question: "How long does a temporary email address last?",
      answer: "All temporary email addresses and their messages automatically expire and are permanently deleted after 24 hours. This ensures your privacy and keeps our service clean and efficient."
    },
    {
      question: "Do I need to create an account?",
      answer: "No, you don't need an account to use temporary email addresses. However, creating a free account allows you to save multiple addresses, access them from any device, and manage them more easily from your dashboard."
    },
    {
      question: "Is Instant-Email really free?",
      answer: "Yes, Instant-Email is 100% free with no hidden costs, no premium tiers, and no subscriptions. We believe privacy should be accessible to everyone."
    },
    {
      question: "Can I send emails from my temporary address?",
      answer: "Currently, Instant-Email only supports receiving emails. You cannot send emails from temporary addresses. Our service is designed for one-way communication to protect your privacy when signing up for services."
    },
    {
      question: "What happens to my emails after 24 hours?",
      answer: "After 24 hours, both your temporary email address and all received messages are automatically and permanently deleted from our servers. This cannot be reversed, so make sure to save any important information before the expiration time."
    },
    {
      question: "Can I extend the expiration time?",
      answer: "Currently, all temporary addresses have a fixed 24-hour lifespan and cannot be extended. If you need longer-term addresses, consider creating a free account where you can generate new addresses and manage them more effectively."
    },
    {
      question: "Is it safe to use temporary email addresses?",
      answer: "Yes, using temporary email addresses is safe for most purposes like newsletter signups, free trials, and testing. However, we don't recommend using them for sensitive accounts like banking, healthcare, or other critical services where you need long-term access."
    },
    {
      question: "Will I get spam or my data sold?",
      answer: "No. We do not sell, trade, or share your data with anyone. We don't track your activity beyond basic analytics to improve our service. Your temporary emails are automatically deleted after 24 hours."
    },
    {
      question: "Can I access my temporary email from multiple devices?",
      answer: "Yes! Your temporary email session is stored in your browser, so you can access it from the same device. If you create an account, you can access your saved addresses from any device by logging in."
    },
    {
      question: "What if I don't receive an email?",
      answer: "Emails typically arrive instantly. If you don't see an email: 1) Wait a few minutes and refresh, 2) Check that you copied the full email address correctly, 3) Some services block temporary email domains - if this happens, try a different service or use a registered account."
    },
    {
      question: "Are there any limitations on what emails I can receive?",
      answer: "While we don't actively filter content, our service should only be used for lawful purposes. We prohibit using our service for illegal activities, receiving illegal content, or circumventing security measures. See our Terms of Service for complete details."
    },
    {
      question: "Can I get the same email address again?",
      answer: "Temporary email addresses are randomly generated and cannot be specifically recreated. Once an address expires, it's gone forever. If you need to keep an address, save it to your account before it expires."
    },
    {
      question: "Do you support attachments?",
      answer: "Yes, emails with attachments are supported. You can view and download attachments from emails received at your temporary address. Remember, all attachments will be deleted along with the email after 24 hours."
    },
    {
      question: "What domains do you use for temporary emails?",
      answer: "We use instant-email.org and several other domains for our temporary email addresses. All our domains are properly configured to receive emails from most services."
    },
    {
      question: "My question isn't answered here. How can I contact you?",
      answer: "We'd love to help! Visit our Contact page or send us an email at support@instant-email.org with your question."
    }
  ];

  return (
    <>
      <SEO 
        title="Frequently Asked Questions"
        description="Find answers to common questions about Instant-Email's temporary disposable email service. Learn how to use temp email addresses safely."
        path="/faq"
      />
      
      <div className="min-h-screen bg-background">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 glass">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Mail className="w-8 h-8 text-primary" />
              <span className="font-heading text-xl font-semibold text-foreground">Instant-Email</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" className="text-foreground hover:text-primary">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </nav>

        {/* Content */}
        <div className="pt-28 pb-16 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <HelpCircle className="w-10 h-10 text-primary" />
              <h1 className="font-heading text-4xl font-bold text-foreground">Frequently Asked Questions</h1>
            </div>
            <p className="text-xl text-muted-foreground mb-12">
              Everything you need to know about Instant-Email
            </p>

            <Card className="bg-card border-border">
              <CardContent className="p-8">
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((faq, index) => (
                    <AccordionItem key={index} value={`item-${index}`}>
                      <AccordionTrigger className="text-left font-semibold">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>

            {/* Still have questions? */}
            <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20 mt-8">
              <CardContent className="p-8 text-center">
                <h2 className="text-2xl font-semibold mb-4">Still have questions?</h2>
                <p className="text-muted-foreground mb-6">
                  Can't find the answer you're looking for? Get in touch with our support team.
                </p>
                <Link to="/contact">
                  <Button className="rounded-full">
                    Contact Support
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default FAQ;
